// Vue core
import Vue from "vue"
import Vuex from "vuex"

// Vue plugins
import Croppa from "vue-croppa"
import ElementUI from "element-ui"
import locale from "element-ui/lib/locale"
import lang from "element-ui/lib/locale/lang/th"
import VeeValidate, { Validator } from "vee-validate"
import messageTH from "vee-validate/dist/locale/th"
import * as VueGoogleMaps from "vue2-google-maps"
import VueTabs from "vue-nav-tabs"
import "vue-nav-tabs/themes/vue-tabs.css"

// Third-party
import Axios from "axios"
import { DateTime } from "luxon"
import moment from "moment"

// Internal
import App from "./App"
import ConfirmDialog from "./components/helper/dialog/ConfirmDialog"
import { baanRueJaiClient } from "./dependencies"
import ClickOutside from "./directives/ClickOutside"
import SafeHtml from "./directives/SafeHtml"
import { isBackgroundRequest } from "./lib/backgroundRequest"
import { Auth, getCookie, setCookie, unsetCookie } from "./lib/cookie"
import { toLocaleTH } from "./lib/dateTime"
import {
  formatCurrencyAndTwoDecimalsString,
  formatToMoney,
} from "./lib/formatter"
import router from "./router"
import i18n from "./setup/lang"

locale.use(lang)

Vue.use(Vuex)
Vue.use(Croppa)
Vue.use(ElementUI)

Vue.mixin({
  computed: {
    $isCallCenter() {
      return +localStorage.getItem("adminRoleId") === 4
    },
    $isAccounting() {
      return (
        JSON.parse(localStorage.getItem("adminRolePermission")).roleName ===
        "Accounting"
      )
    },
  },
})

Validator.localize("th", messageTH)
Vue.use(VeeValidate, {
  locale: "th",
})

export const store = new Vuex.Store({
  state: {
    sideBar: false,
    loader: false,
    projectId: localStorage.getItem("projectId"),
    projectName: localStorage.getItem("projectName"),
    projectIcon: localStorage.getItem("projectIcon"),
    notification: [],
  },
  mutations: {
    sideBarToggle: (state) => !state.sideBar,
    resetNotification(state) {
      state.notification = []
    },
    addNotification(state, payload) {
      state.notification.push(payload)
    },
    loaderStart(state) {
      state.loader = true
    },
    loaderStop(state) {
      state.loader = false
    },
    setProjectName(state, name) {
      state.projectName = name
    },
    setProjectId(state, id) {
      state.projectId = id
    },
  },
})

Axios.interceptors.request.use((config) => {
  // Do something before request is sent
  store.commit("loaderStart")
  return config
}, null)

Axios.interceptors.response.use(
  (res) => {
    store.commit("loaderStop")
    return res
  },
  (error) => {
    store.commit("loaderStop")
    const isIidGoogleApisUrl = (url) => {
      try {
        return new URL(url).hostname === "iid.googleapis.com"
      } catch (err) {
        return false
      }
    }
    const isAdminSessionHostUrl = (url) =>
      typeof url === "string" && url.startsWith(process.env.HOST)

    if (
      error.config &&
      error.response &&
      !isIidGoogleApisUrl(error.response.request.responseURL)
    ) {
      const isSupplierUrl =
        router.currentRoute.path.split("/")[1] === "partners"

      if (
        error.response.status === 401 &&
        error.response.config.url.indexOf("retoken") <= 0
      ) {
        let routeName
        const { config } = error
        let refreshToken

        if (isSupplierUrl) {
          routeName = "SupplierLogin"
          refreshToken = localStorage.getItem("supplierRefreshToken")
        } else {
          routeName = "Login"
          const cookieRefreshToken = getCookie(Auth.RefreshToken)
          if (cookieRefreshToken) {
            refreshToken = cookieRefreshToken
          } else {
            refreshToken = localStorage.getItem("refreshToken")
          }
        }

        return Axios.post(
          `${process.env.HOST}/api/admin/auth/retoken`,
          {},
          {
            headers: {
              Authorization: `Bearer ${refreshToken}`,
            },
          },
        )
          .then((response) => {
            if (response.status !== 200) {
              router.push({ name: routeName })
              return null
            }

            const { accessToken } = response.data.accessToken
            if (isSupplierUrl) {
              localStorage.setItem("supplierAccessToken", accessToken)
            } else {
              setCookie(Auth.AccessToken, accessToken)
            }

            config.headers.Authorization = `Bearer ${accessToken}`
            baanRueJaiClient.setAuthorizationToken(accessToken)

            return Axios.request(config)
          })
          .catch(() => {
            router.push({ name: routeName })
          })
      }

      if (
        error.response.status === 403 &&
        isAdminSessionHostUrl(error.response.config.url) &&
        !isBackgroundRequest(error.config)
      ) {
        localStorage.removeItem("adminName")
        localStorage.removeItem("adminRole")
        localStorage.removeItem("adminRolePermission")
        localStorage.removeItem("adminProjects")
        localStorage.removeItem("accessToken")
        localStorage.removeItem("refreshToken")
        unsetCookie(Auth.AccessToken)
        unsetCookie(Auth.RefreshToken)

        router.push({ name: isSupplierUrl ? "SupplierLogin" : "Login" })
      }
    }

    throw error
  },
)

Vue.config.productionTip = false

Vue.use(VueGoogleMaps, {
  load: {
    key: "AIzaSyBOb6S8qelddMiLfZQXhBSGozL3-7DNFJA",
    libraries: "places",
    loading: "async",
  },
})

moment.locale("th")
const formatMomentFull = (text) => moment(text).format("LLL")
const formatMomentDatetime = (text) => moment(text).format("D MMM HH:mm")

Vue.filter("moment", (text) => moment(text).fromNow())
Vue.filter("moment-full", formatMomentFull)
Vue.filter("momentFull", formatMomentFull)
Vue.filter("moment-day-month", (text) => moment(text).format("D MMMM"))
Vue.filter("moment-time", (text) => moment(text).format("HH:mm"))
Vue.filter("moment-datetime", formatMomentDatetime)
Vue.filter("momentDatetime", formatMomentDatetime)
Vue.filter("moment-day", (text) => moment(text).format("D"))
Vue.filter("moment-month", (text) => moment(text).format("MMM"))
Vue.filter("sentiment-score", (text) => (text > 3 ? "😡" : "🙂"))
Vue.filter("contact-category", (text) => {
  if (text === 1) {
    return "พูดคุยสอบถาม"
  }
  return text
})
Vue.filter("contact_id", (text) => {
  let output = text.toString()
  if (output.length >= 6) return output

  while (output.length < 6) {
    output = `0${output}`
  }
  return `#${output}`
})
Vue.filter("article-content", (text) => {
  if (!text) {
    return i18n.tc("feature.article.no_content")
  }
  const obj = JSON.parse(text)
  let output = ""
  obj.forEach((element) => {
    output += element.insert
  })
  return output
})
Vue.filter("card", (text) => {
  if (text === "topic") {
    return "/static/icons/topic.svg"
  }

  if (text === "event") {
    return "/static/icons/movie.svg"
  }
  if (text === "call") {
    return "/static/icons/phone.svg"
  }
  if (text === "water") {
    return "/static/icons/water.svg"
  }
  if (text === "visitor") {
    return "/static/icons/link.svg"
  }
  if (text === "package") {
    return "/static/icons/package.svg"
  }
  return "/static/icons/topic.svg"
})
Vue.filter("correct", (text) => {
  if (text) {
    return "/static/icons/correct.svg"
  }

  return "/static/icons/wrong.svg"
})
Vue.filter("owner", (text) => {
  if (text === "owner") {
    return i18n.tc("owner.owner")
  }

  if (text === "renter") {
    return i18n.tc("owner.renter")
  }

  if (text === "resident") {
    return i18n.tc("owner.resident")
  }

  if (text === "contract") {
    return i18n.tc("owner.contract")
  }

  if (text === "booking") {
    return i18n.tc("owner.booking")
  }

  return i18n.tc("owner.visitor")
})
Vue.filter("job-no", (text) => text)

Vue.component("ConfirmDialog", ConfirmDialog)
Vue.directive("click-outside", ClickOutside)
Vue.directive("safe-html", SafeHtml)

Vue.filter("money", (value, emptyValue) => {
  if (!value && emptyValue !== undefined) {
    return emptyValue
  }

  return formatToMoney(value)
})

Vue.filter("commas", (value, emptyValue) => {
  if (!value && emptyValue !== undefined) {
    return emptyValue
  }

  return formatCurrencyAndTwoDecimalsString(value)
})

Vue.filter("date", (value, format = "d/M/yyyy") => {
  if (!value) return ""

  const dateTime = toLocaleTH(DateTime.fromISO(value))

  if (dateTime.invalid) return "-"
  return dateTime.toFormat(format)
})

const fixElSelect = () => {
  document
    .querySelectorAll('.el-select[data-is-search="true"]:hover')
    .forEach(() => {
      const elInput = document.querySelector(
        '.el-select[data-is-search="true"]:hover input[readonly]',
      )
      if (elInput) {
        elInput.readOnly = false
        elInput.blur()
        elInput.focus()
      }
    })
}
document.addEventListener("focusin", fixElSelect)
document.addEventListener("click", fixElSelect)
document.addEventListener("touchstart", fixElSelect)
Vue.use(VueTabs)

/* eslint-disable no-new */
new Vue({
  el: "#app",
  router,
  components: {
    App,
  },
  template: "<App/>",
  store,
  i18n,
})
